<template>
  <div :class="{ 'cursor-pointer': notification.onAction }" @click="action">
    <div class="flex flex-col shadow mb-3 transparent-background rounded-xl py-4 px-6 z-50">
      <div class="flex items-center justify-between">
        <div :class="'bg-' + (notification.iconColor || 'gray-400')" class="rounded-full p-2">
          <icon :name="notification.icon || 'default-notification'" class="text-white" />
        </div>
        <div class="flex flex-col flex-1 ml-4 py-2">
          <strong class="text-white">{{ notification.title }}</strong>
        </div>
        <div
          v-if="notification.type === 'progress-bar' && notification.progress < 100"
          class="flex justify-end text-white cursor-pointer"
          @click="cancelProgress"
        >
          <div>{{ $t('notification.cancel') }}</div>
        </div>
        <div
          v-else-if="!allowBlockDelete || !notification.blockDelete"
          class="flex justify-end w-12"
        >
          <icon name="close" class="text-red cursor-pointer" @click.stop="deactivate" />
        </div>
      </div>
      <div
        v-if="notification.type === 'progress-bar'"
        class="flex items-center justify-between py-3"
      >
        <div class="px-2">
          <div class="w-6"></div>
        </div>
        <div class="w-full h-4 bg-violet-100 rounded-full">
          <div
            class="flex items-center justify-center h-4 bg-violet rounded-full progress"
            :style="progressStyles"
          >
            <div v-if="notification.progress >= 10" class="text-center text-xs text-white">
              {{ notification.progress }}%
            </div>
          </div>
        </div>
      </div>
      <div v-if="notification.subtitle || notification.createdAt" class="w-80">
        <div v-if="notification.subtitle" class="flex text-sm text-white ml-14 capitalize-first">
          <div v-if="notification.subtitleIcon" class="pr-2">
            <icon :name="notification.subtitleIcon || 'default-notification'" class="text-white" />
          </div>
          <div>{{ notification.subtitle }}</div>
        </div>
        <div v-if="notification.createdAt">
          <div v-if="notification.keepInScreen" class="text-sm text-end text-gray-400">
            {{ timeAgo }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import { lastUtils } from '@last/core'
import Icon from './Icon.vue'

export default {
  name: 'LNotification',
  components: {
    Icon
  },
  filters: {
    fromNow(date) {
      return moment(date).fromNow()
    }
  },
  props: {
    notification: {
      type: Object,
      default: null
    },
    allowBlockDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ['notificationDeleted', 'progressCanceled'],
  data: () => ({
    timeAgo: '',
    interval: null
  }),
  computed: {
    progressStyles() {
      return {
        width: `${this.notification.progress}%`
      }
    }
  },
  watch: {
    notification: {
      async handler(notification) {
        if (notification.progress >= 100) {
          await lastUtils.sleep(500)
          this.deactivate()
        }
      },
      deep: true,
      immediate: true
    }
  },
  unmounted() {
    clearInterval(this.interval)
  },
  mounted() {
    this.calculateTimeAgo()
    this.interval = setInterval(this.calculateTimeAgo, 60000)
    if (!this.notification.keepInScreen && this.notification.type !== 'progress-bar') {
      setTimeout(this.deactivate, this.notification.timeout || 3000)
    }
  },
  methods: {
    deactivate() {
      this.$emit('notificationDeleted')
    },
    action() {
      if (this.notification.onAction) {
        this.notification.onAction()
        this.deactivate()
      }
    },
    calculateTimeAgo() {
      this.timeAgo = moment(this.notification.createdAt).fromNow()
    },
    cancelProgress() {
      this.$emit('progressCanceled')
      this.deactivate()
    }
  }
}
</script>

<style scoped>
.transparent-background {
  background-color: rgba(64, 63, 100, 0.8);
  backdrop-filter: blur(0.625rem);
}
.rounded-xl {
  border-radius: 1rem;
}
.capitalize-first::first-letter {
  text-transform: uppercase;
}
.progress {
  transition: 0.4s linear;
  transition-property: width;
}
</style>
